import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';

import { RegisterComponent } from '../../components/component-lookup';
import { StateService } from '../../services/state.service';
import { MetricService, QuarterlyInstitution, QuarterlyInstitutionDetail } from '../../services/metric.service';
import { MetricQuarterlyInstitutionComponent } from '../../visuals/metric-quarterly-institution/metric-quarterly-institution.component';
import { VisualService } from '../../services/visual.service';

@RegisterComponent('QuarterlyInstitution')
@Component({
  selector: 'app-quarterly-institution',
  standalone: true,
  imports: [CommonModule, DatePipe, MetricQuarterlyInstitutionComponent],
  templateUrl: './quarterly-institution.component.html',
  styleUrl: './quarterly-institution.component.scss'
})
export class QuarterlyInstitutionComponent {
  paywall = input<boolean>();
  paywallMessage = input<string | null>();

  private metricService = inject(MetricService);
  readonly state = inject(StateService).state;
  readonly institution = inject(StateService).institution;
  readonly peerInstitutions = inject(StateService).peerInstitutions;
  readonly date = inject(StateService).date;

  readonly isPrinting = inject(StateService).isPrinting;

  metrics = signal<QuarterlyInstitution[]>([]);
  groupedMetrics = computed(() => this.groupBy(this.computedMetrics(), m => m.sectionTitle));
  sections = computed(() => Object.keys(this.groupedMetrics()));

  computedMetrics = computed(() => {
    this.metrics().forEach(metric => {
      const details = metric.details ?? [];
      metric.details = this.peerInstitutions().map(peer => {
        const peerDetails = details.find(d => d.iid == peer.iid) ?? { iid: peer.iid, value: 0 } as QuarterlyInstitutionDetail;
        return {
          iid: peer.iid,
          value: peerDetails.value,
          difference: ((Math.round(((metric.institutionValue ?? 0) + Number.EPSILON) * 100) / 100) - (Math.round((peerDetails.value + Number.EPSILON) * 100) / 100)) * (metric.rankSort == 'L' ? -1 : 1)
        } as QuarterlyInstitutionDetail;
      });
    });
    return this.metrics();
  });

  metric?: QuarterlyInstitution;
  loading = false;
  isDrawerOpen = false;

  constructor() {
    effect(() => {
      const peerIids = this.peerInstitutions().map(p => p.iid);
      const date = this.date();
      if (date) {
        this.loading = true;
        this.metricService.getInstitutionQuarterlySummary(this.institution().iid, date, peerIids)?.subscribe(metrics => {
          setTimeout(() => {
            this.metrics.set(metrics);
            this.loading = false;
          });
        });
      }
    });
  }

  openDrawer() {
    this.isDrawerOpen = true;
  }

  closeDrawer() {
    this.isDrawerOpen = false;
    if (this.metric) {
      this.metric.isSelected = false;
    }
  }

  onToggleDetail(metric: QuarterlyInstitution) {
    if (this.isDrawerOpen) {
      if (this.metric == metric) {
        this.closeDrawer();
      }
      else {
        if (this.metric) {
          this.metric.isSelected = false;
        }
        this.updateChart(metric);
      }
    }
    else {
      this.openDrawer();
      this.updateChart(metric);
    }
  }

  updateChart(metric: QuarterlyInstitution) {
    this.metric = metric;
    this.metric.isSelected = true;
  }

  getDisplayTitle(metric: QuarterlyInstitution): string {
    const indent = '&nbsp;'.repeat(metric.level * 5);
    return `${indent}${metric.rowTitle}`;
  }

  getDisplayValue(displayType: string, value: number): string {
    return VisualService.getValueDisplay(VisualService.getValue(value, displayType) ?? 0, displayType);
  }

  getDisplayDifference(displayType: string, value: number): string {
    const v = VisualService.getValueDisplay(VisualService.getValue(value, displayType) ?? 0, displayType);
    return v.charAt(0) == '-' ? v.substring(1) : v;
  }

  groupBy<T>(arr: T[], fn: (item: T) => string) {
    return arr.reduce<Record<string, T[]>>((prev, curr) => {
      const groupKey = fn(curr);
      const group = prev[groupKey] || [];
      group.push(curr);
      return { ...prev, [groupKey]: group };
    }, {});
  }
}
